import { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { describeApiError } from '@/lib/api/errors';
import { useCreateXrefMutation, type CreateXrefBody } from '@/lib/api/readiness-xref';
import { useDeviationsListQuery } from '@/lib/api/deviations';
import { useEdlListQuery } from '@/lib/api/deliverables';
import {
  extractFindingPayloadString,
  useFindingListQuery,
  type FindingListItem,
} from '@/lib/api/health-findings';
import { useHealthDomainsQuery, useDomainAssessmentsQuery } from '@/lib/api/health-domains';
import type { RubricCriterion } from '@/lib/api/readiness-rubric';
import { titleCase } from '@/lib/utils/format';
import { cn } from '@/lib/utils/cn';

/**
 * Link a readiness criterion to a source entity in Contract Compliance or
 * Health Check (Tier 2 — E08-F07 xref create). Kind → module is fixed by
 * the API's coherence matrix: deviation/deliverable → contract_compliance;
 * finding/domain_rating → health_check.
 */
const KIND_TO_MODULE: Record<string, CreateXrefBody['source_module']> = {
  deviation: 'contract_compliance',
  deliverable: 'contract_compliance',
  finding: 'health_check',
  domain_rating: 'health_check',
};

/** The form's field labels, so a validation error names the field (C2-31). */
const FIELDS: Record<string, string> = {
  criterion_id: 'Readiness criterion',
  source_entity_kind: 'Evidence type',
  source_entity_id: 'Source record',
  source_module: 'Evidence type',
};

const MAX_LABEL_CHARS = 80;

/**
 * A finding by its words, never a short id (C2-31): its title or name, else
 * the start of its description, else its category and severity.
 */
export function findingOptionLabel(
  f: Pick<FindingListItem, 'payload' | 'category' | 'severity'>,
): string {
  const words =
    extractFindingPayloadString(f.payload, 'title') ??
    extractFindingPayloadString(f.payload, 'name') ??
    extractFindingPayloadString(f.payload, 'description');
  if (words) {
    return words.length > MAX_LABEL_CHARS ? `${words.slice(0, MAX_LABEL_CHARS - 1)}…` : words;
  }
  const kind = `${titleCase(f.category ?? 'general')} finding`;
  return f.severity ? `${kind} · ${titleCase(f.severity)}` : kind;
}

/** Two records with the same words read "Name", "Name (2)", ... so each option is told apart. */
function disambiguate(options: Array<{ id: string; label: string }>) {
  const seen = new Map<string, number>();
  return options.map((o) => {
    const n = (seen.get(o.label) ?? 0) + 1;
    seen.set(o.label, n);
    return n === 1 ? o : { ...o, label: `${o.label} (${n})` };
  });
}

export function LinkXrefModal({
  engagementId,
  criteria,
  open,
  onOpenChange,
  defaultCriterionId,
}: {
  engagementId: string;
  criteria: RubricCriterion[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Preselects the criterion when the dialog is opened from that criterion's pane. */
  defaultCriterionId?: string;
}) {
  const [criterionId, setCriterionId] = useState(defaultCriterionId ?? '');
  const [kind, setKind] = useState<CreateXrefBody['source_entity_kind']>('deviation');
  const [entityId, setEntityId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateXrefMutation(engagementId);

  const deviations = useDeviationsListQuery(
    open && kind === 'deviation' ? engagementId : undefined,
  );
  const edl = useEdlListQuery(open && kind === 'deliverable' ? engagementId : undefined);
  const findings = useFindingListQuery(open && kind === 'finding' ? engagementId : undefined);
  const domains = useHealthDomainsQuery(
    open && kind === 'domain_rating' ? engagementId : undefined,
  );
  const assessments = useDomainAssessmentsQuery(
    open && kind === 'domain_rating' ? engagementId : undefined,
  );

  const options = useMemo<Array<{ id: string; label: string }>>(() => {
    if (kind === 'deviation') {
      return (deviations.data?.items ?? []).map((d) => ({
        id: d.deviation_entry_id,
        label: d.title,
      }));
    }
    if (kind === 'deliverable') {
      return (edl.data?.entries ?? []).map((d) => ({
        id: d.register_entry_id,
        label: d.sow_ref ? `${d.sow_ref} · ${d.title}` : d.title,
      }));
    }
    if (kind === 'finding') {
      return disambiguate(
        (findings.data?.items ?? []).map((f) => ({ id: f.id, label: findingOptionLabel(f) })),
      );
    }
    const domainName = new Map((domains.data?.domains ?? []).map((d) => [d.id, d.name]));
    return (assessments.data?.items ?? [])
      .filter((a) => a.status === 'committed' || a.rating)
      .map((a) => ({
        id: a.assessment_id,
        label: `${domainName.get(a.domain_id) ?? 'Domain'} — round ${a.round_seq}${
          a.rating ? ` (${titleCase(a.rating)})` : ''
        }`,
      }));
  }, [kind, deviations.data, edl.data, findings.data, domains.data, assessments.data]);

  const optionsLoading =
    deviations.isLoading || edl.isLoading || findings.isLoading || assessments.isLoading;

  function submit() {
    setError(null);
    create.mutate(
      {
        body: {
          criterion_id: criterionId,
          source_module: KIND_TO_MODULE[kind]!,
          source_entity_kind: kind,
          source_entity_id: entityId,
        },
        idempotencyKey: crypto.randomUUID(),
      },
      {
        onSuccess: () => {
          setEntityId('');
          onOpenChange(false);
        },
        onError: (e) => setError(describeApiError(e, FIELDS, 'Link failed.')),
      },
    );
  }

  const inputCls =
    'mt-1 w-full rounded-card border border-ink-200 bg-paper-0 px-2 py-1.5 text-body-sm text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-scrim" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[min(520px,94vw)] -translate-x-1/2 -translate-y-1/2',
            'rounded-modal bg-paper-0 shadow-modal',
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-ink-100 bg-paper-50 px-5 py-4">
            <div>
              <Dialog.Title className="text-h2 font-semibold text-ink-900">
                Link cross-module evidence
              </Dialog.Title>
              <p className="mt-1 text-meta text-ink-500">
                Binds a criterion to live evidence; the value freezes at snapshot capture.
              </p>
            </div>
            <Dialog.Close
              aria-label="Close"
              className="inline-flex h-8 w-8 items-center justify-center rounded-card text-ink-500 hover:bg-paper-100 hover:text-ink-900"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Dialog.Close>
          </div>

          <div className="space-y-4 px-5 py-4">
            <label className="block text-meta font-medium text-ink-700">
              Readiness criterion
              <select
                value={criterionId}
                onChange={(e) => setCriterionId(e.target.value)}
                className={inputCls}
              >
                <option value="">— select a criterion —</option>
                {criteria.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({titleCase(c.category)})
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-meta font-medium text-ink-700">
              Evidence type
              <select
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value as CreateXrefBody['source_entity_kind']);
                  setEntityId('');
                }}
                className={inputCls}
              >
                <option value="deviation">Deviation (Contract Compliance)</option>
                <option value="deliverable">Deliverable (Contract Compliance)</option>
                <option value="finding">Finding (Health Check)</option>
                <option value="domain_rating">Domain rating (Health Check)</option>
              </select>
            </label>
            <label className="block text-meta font-medium text-ink-700">
              Source record
              <select
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                className={inputCls}
              >
                <option value="">
                  {optionsLoading ? 'Loading…' : `— select a ${kind.replace('_', ' ')} —`}
                </option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
              {!optionsLoading && options.length === 0 ? (
                <span className="mt-1 block text-accent-amber-700">
                  No {kind.replace('_', ' ')} records exist in this engagement yet.
                </span>
              ) : null}
            </label>
            {error ? (
              <p className="rounded-card border border-cite-broken bg-paper-0 p-3 text-meta font-medium text-cite-broken">
                {error}
              </p>
            ) : null}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-ink-100 bg-paper-50 px-5 py-3">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="sm">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              type="button"
              size="sm"
              disabled={!criterionId || !entityId || create.isPending}
              onClick={submit}
            >
              {create.isPending ? <Spinner /> : null}
              Link evidence
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
